import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { refreshInventory } from "form/inventory/tools/common"
import { setTempShapeFeature } from "form/map/tools/geo-targets-shape"
import { IForm, IFormChange, IFormDataLoader, IFormGeoTargets, IFormRelations, IGeoTargetsSelection } from "form/model"
import { formActionCreator } from "form/store/actions"
import { getFormData } from "form/store/state"
import { getForm, setFormGeoTargets, setFormRelations } from "form/tools/common"
import { messageSVC } from "message/service"
import { getCitiesWithoutMetropolitanCities, getMetropolitansFromCities } from "shape/tools/metropolitan"
import {
  IGeoShape,
  IGeoShapeCity,
  IGeoShapeCountry,
  IGeoShapeMetropolitan,
  IGeoShapePostalCode,
  IGeoShapeState,
  SHAPE_TYPES
} from "../../shape/model"
import { shapeRPO } from "../../shape/repository"
import {
  fetchAndSetShapeGeometry,
  getShapeId,
  notifyShapeDelete,
  notifyShapesAdd,
  notifyShapesDelete
} from "../../shape/tools/common"
import { finalizeChange, getEmptyFormChange } from "./change"
import { getGeoTargetsChanges, getGeoTargetsForm, setGeoTargetsForm, setGeoTargetsFormSelection } from "./geo-targets"

export function getShapesChanges(form: IForm): IFormChange[] {
  const previousCities = form.context.initialData
    ? getCitiesWithoutMetropolitanCities(
        form.context.initialData.geoTargets.cities,
        form.context.initialData.geoTargets.metropolitans
      )
    : []
  const previousPostalCodes = form.context.initialData ? form.context.initialData.geoTargets.postalCodes : []
  const previousStates = form.context.initialData ? form.context.initialData.geoTargets.states : []
  const previousCountries = form.context.initialData ? form.context.initialData.geoTargets.countries : []
  const previousMetropolitans = form.context.initialData ? form.context.initialData.geoTargets.metropolitans : []
  return [
    getShapeChange(
      "campaign.geo-targets.shape.city.title",
      previousCities,
      getCitiesWithoutMetropolitanCities(form.geoTargets.cities, form.geoTargets.metropolitans)
    ),
    getShapeChange("campaign.geo-targets.shape.postal-code.title", previousPostalCodes, form.geoTargets.postalCodes),
    getShapeChange("campaign.geo-targets.shape.state.title", previousStates, form.geoTargets.states),
    getShapeChange("campaign.geo-targets.shape.country.title", previousCountries, form.geoTargets.countries),
    getShapeChange(
      "campaign.geo-targets.shape.metropolitan.title",
      previousMetropolitans,
      form.geoTargets.metropolitans
    )
  ]
}

function getShapeChange(labelKey: string, previousShapes: IGeoShape[], newShapes: IGeoShape[]): IFormChange {
  const change = getEmptyFormChange(labelKey)
  for (const newShape of newShapes) {
    const oldShape = previousShapes.findByProperty("id", newShape.id)
    if (!oldShape) {
      change.addedItems.push(String(newShape.id))
      continue
    }
  }
  for (const oldShape of previousShapes) {
    if (!newShapes.findByProperty("id", oldShape.id)) {
      change.deletedItems.push(String(oldShape.id))
    }
  }
  return finalizeChange(change)
}

export const citiesFormDataLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const cities = await shapeRPO.getAssociatedCities(form.tempCampaignId)
    form.context.relations.cities = cities
    form.geoTargets.cities = cities
    form.geoTargets.metropolitans = getMetropolitansFromCities(cities)
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Cities data-loader error ${String(error)}`)
  }
}

export const postalCodesFormDataLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const postalCodes = await shapeRPO.getAssociatedPostalCodes(form.tempCampaignId)
    form.context.relations.postalCodes = postalCodes
    form.geoTargets.postalCodes = postalCodes
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Postal codes data-loader error ${String(error)}`)
  }
}

export const statesFormDataLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const states = await shapeRPO.getAssociatedStates(form.tempCampaignId)
    form.context.relations.states = states
    form.geoTargets.states = states
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`States data-loader error ${String(error)}`)
  }
}

export const countriesFormDataLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const countries = await shapeRPO.getAssociatedCountries(form.tempCampaignId)
    form.context.relations.countries = countries
    form.geoTargets.countries = countries
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Countries data-loader error ${String(error)}`)
  }
}

export const selectShapeInForm = async (selectedShape: IGeoShape | null): Promise<IGeoShape | null> => {
  if (selectedShape) {
    setGeoTargetsForm({ isSelectingShape: true })
    try {
      selectedShape = await fetchAndSetShapeGeometry(selectedShape)
    } catch (error) {
      console.error(error)
      messageSVC.error(translate("campaign.geo-targets.shape.select.error"))
      setGeoTargetsForm({ isSelectingShape: false })
      return selectedShape
    }
  }
  setGeoTargetsForm({ ...getGeoTargetsForm(), toAddShape: selectedShape, isSelectingShape: false })
  setTempShapeFeature(selectedShape)
  return selectedShape
}

export async function addShapes(shapes: IGeoShape[], avoidAddChanges?: boolean): Promise<IGeoShape[]> {
  let form = getForm()
  if (shapes.length > 1) {
    shapes = shapes.filter(s => !isShapeAlreadySelected(s, form))
  }
  if (shapes.length === 0) {
    return Promise.resolve([])
  }
  const shapeType = shapes[0].type
  const newFormShapes = getFormShapes(form, shapeType).slice()
  for (const shape of shapes) {
    shape.isProcessingServerSide = true
    newFormShapes.push(shape)
  }
  getStore().dispatch(
    formActionCreator.setForm({
      form: getNewFormForShapes(form, shapeType, newFormShapes)
    })
  )
  try {
    const addedShapes: IGeoShape[] = []
    let newServerSideShapes: IGeoShape[] | undefined = []
    if (shapes.length === 1) {
      const shape = shapes[0]
      const newShape = await createNewShapeEntity(shape, form.tempCampaignId)
      form = getForm()
      shape.id = newShape.id
      shape.isProcessingServerSide = false
      addedShapes.push(newShape)
      newServerSideShapes = [...getShapeRelations(form, shapeType), newShape]
    } else {
      const newAddedShapes = await addAllShapesForType(form, shapeType, shapes, form.tempCampaignId)
      form = getForm()
      addedShapes.push(...newAddedShapes)
      for (const shape of shapes) {
        shape.id = getSameShapeInArray(shape, newAddedShapes).id
        shape.isProcessingServerSide = false
      }
      newServerSideShapes = [...getShapeRelations(form, shapeType), ...newAddedShapes]
    }
    const newForm = getNewFormForShapes(form, shapeType, newFormShapes.slice(), newServerSideShapes)
    getStore().dispatch(
      formActionCreator.setForm({
        form: newForm,
        changes: !avoidAddChanges
          ? {
              geoTargets: getGeoTargetsChanges(newForm)
            }
          : undefined,
        lastSavedData: getFormData(form)
      })
    )
    refreshInventory()
    notifyShapesAdd(addedShapes)
    return addedShapes
  } catch (error) {
    console.error(error)
    // an error occured we set back shapes
    getStore().dispatch(
      formActionCreator.setForm({ form: getNewFormForShapes(form, shapeType, getFormShapes(form, shapeType)) })
    )
    messageSVC.error(translate(`campaign.geo-targets.error.${shapeType}.add`))
    return Promise.reject("unable to create shape")
  }
}

export async function deleteShape(shape: IGeoShape, avoidAddChanges?: boolean): Promise<void> {
  let form = getForm()
  shape.isProcessingServerSide = true
  const shapeType = shape.type
  const newFormShapes = getFormShapes(form, shapeType).map(s => (areEqualShapes(s, shape) ? shape : s))
  getStore().dispatch(
    formActionCreator.setForm({
      form: getNewFormForShapes(form, shapeType, newFormShapes)
    })
  )
  try {
    await deleteShapeEntity(shape, form.tempCampaignId)
    form = getForm()
    const newForm = getNewFormForShapes(
      form,
      shapeType,
      newFormShapes.filter(s => !areEqualShapes(s, shape)),
      getShapeRelations(form, shapeType).filter(s => !areEqualShapes(s, shape))
    )
    getStore().dispatch(
      formActionCreator.setForm({
        form: newForm,
        changes: !avoidAddChanges
          ? {
              geoTargets: getGeoTargetsChanges(newForm)
            }
          : undefined,
        lastSavedData: getFormData(form)
      })
    )
    const geoTargetsForm = getGeoTargetsForm()
    if (geoTargetsForm.selection) {
      // if shape is selected (in list multiple selection) we unselect it
      const selectedIds = getSelectionForShapeType(geoTargetsForm.selection, shape.type)
      const externalId = getShapeId(shape)
      if (selectedIds.length > 0 && selectedIds.includes(externalId)) {
        const newSelection = setSelectionForShapeType(
          { ...geoTargetsForm.selection },
          shape.type,
          selectedIds.filter(id => id !== externalId)
        )
        getStore().dispatch(
          formActionCreator.setGeoTargetsForm(form.context.appContext, {
            selection: newSelection
          })
        )
      }
    }
    refreshInventory()
    notifyShapeDelete(shape)
  } catch (error) {
    console.error("unable to delete shape", error)
    // an error occured we set back shapes
    shape.isProcessingServerSide = false
    getStore().dispatch(
      formActionCreator.setForm({ form: getNewFormForShapes(form, shapeType, getFormShapes(form, shapeType)) })
    )
    messageSVC.error(translate("campaign.geo-targets.error.shape.delete"))
  }
}

export function deleteShapes(shapes: IGeoShape[]): Promise<void> {
  return new Promise(resolve => {
    let form = getForm()
    const toProcessPromises = shapes.length
    const deletedShapes: IGeoShape[] = []
    const deletedCityIds: string[] = []
    const deletedPostalCodeIds: string[] = []
    const deletedStateIds: string[] = []
    const deletedCountryIds: string[] = []
    const deletedMetropolitanIds: string[] = []
    let processedPromises = 0
    let hasError: boolean = false
    const onFinish = (shape: IGeoShape, error: boolean) => {
      if (!error) {
        deletedShapes.push(shape)
        switch (shape.type) {
          case SHAPE_TYPES.CITY: {
            deletedCityIds.push((shape as IGeoShapeCity).external_id)
            break
          }
          case SHAPE_TYPES.POSTAL_CODE: {
            deletedPostalCodeIds.push((shape as IGeoShapePostalCode).postal_code)
            break
          }
          case SHAPE_TYPES.STATE: {
            deletedStateIds.push((shape as IGeoShapeState).state_shape_id)
            break
          }
          case SHAPE_TYPES.COUNTRY: {
            deletedCountryIds.push((shape as IGeoShapeCountry).country_shape_id)
            break
          }
          case SHAPE_TYPES.METROPOLITAN: {
            deletedMetropolitanIds.push((shape as IGeoShapeMetropolitan).shape_id)
            break
          }
        }
      } else {
        hasError = true
      }
      if (toProcessPromises === ++processedPromises) {
        form = getForm()
        // update shapes relations in store
        setFormRelations({
          cities:
            deletedCityIds.length > 0
              ? (getShapeRelations(form, SHAPE_TYPES.CITY).filter(
                  c => !deletedCityIds.includes((c as IGeoShapeCity).external_id)
                ) as IGeoShapeCity[])
              : form.context.relations.cities,
          postalCodes:
            deletedPostalCodeIds.length > 0
              ? (getShapeRelations(form, SHAPE_TYPES.POSTAL_CODE).filter(
                  p => !deletedPostalCodeIds.includes((p as IGeoShapePostalCode).postal_code)
                ) as IGeoShapePostalCode[])
              : form.context.relations.postalCodes,
          states:
            deletedStateIds.length > 0
              ? (getShapeRelations(form, SHAPE_TYPES.STATE).filter(
                  p => !deletedStateIds.includes((p as IGeoShapeState).state_shape_id)
                ) as IGeoShapeState[])
              : form.context.relations.states,
          countries:
            deletedCountryIds.length > 0
              ? (getShapeRelations(form, SHAPE_TYPES.COUNTRY).filter(
                  p => !deletedCountryIds.includes((p as IGeoShapeCountry).country_shape_id)
                ) as IGeoShapeCountry[])
              : form.context.relations.countries,
          metropolitans:
            deletedMetropolitanIds.length > 0
              ? (getShapeRelations(form, SHAPE_TYPES.METROPOLITAN).filter(
                  p => !deletedMetropolitanIds.includes((p as IGeoShapeMetropolitan).shape_id)
                ) as IGeoShapeMetropolitan[])
              : form.context.relations.metropolitans
        })
        // update shapes form in store
        setFormGeoTargets({
          cities:
            deletedCityIds.length > 0
              ? form.geoTargets.cities.filter(c => !deletedCityIds.includes(c.external_id))
              : form.geoTargets.cities,
          postalCodes:
            deletedPostalCodeIds.length > 0
              ? form.geoTargets.postalCodes.filter(p => !deletedPostalCodeIds.includes(p.postal_code))
              : form.geoTargets.postalCodes,
          states:
            deletedStateIds.length > 0
              ? form.geoTargets.states.filter(s => !deletedStateIds.includes(s.state_shape_id))
              : form.geoTargets.states,
          countries:
            deletedCountryIds.length > 0
              ? form.geoTargets.countries.filter(c => !deletedCountryIds.includes(c.country_shape_id))
              : form.geoTargets.countries,
          metropolitans:
            deletedMetropolitanIds.length > 0
              ? form.geoTargets.metropolitans.filter(m => !deletedMetropolitanIds.includes(m.shape_id))
              : form.geoTargets.metropolitans
        })
        // update selection in store
        const { selection } = getGeoTargetsForm()
        setGeoTargetsFormSelection({
          cityIds: selection.cityIds.filter(id => !deletedCityIds.includes(id)),
          postalCodeIds: selection.postalCodeIds.filter(id => !deletedPostalCodeIds.includes(id)),
          stateIds: selection.stateIds.filter(id => !deletedStateIds.includes(id)),
          countryIds: selection.countryIds.filter(id => !deletedCountryIds.includes(id)),
          metropolitanIds: selection.metropolitanIds.filter(id => !deletedMetropolitanIds.includes(id))
        })
        // display a message if some entities has not been deleted
        if (hasError) {
          messageSVC.error(translate("campaign.geo-targets.delete.multiple.error"))
        }
        notifyShapesDelete(deletedShapes)
        resolve()
      }
    }
    for (const shape of shapes) {
      deleteShapeEntity(shape, form.tempCampaignId)
        .then(() => onFinish(shape, false))
        .catch(error => {
          console.error(error)
          onFinish(shape, true)
        })
    }
  })
}

export function areEqualShapes(s1: IGeoShape, s2: IGeoShape): boolean {
  if (!s1 || !s2 || typeof s1 !== "object" || typeof s2 !== "object" || s1.type !== s2.type) {
    return false
  }
  switch (s1.type) {
    case SHAPE_TYPES.CITY: {
      const shape1 = s1 as IGeoShapeCity
      const shape2 = s2 as IGeoShapeCity
      return shape1.external_id === shape2.external_id
    }
    case SHAPE_TYPES.POSTAL_CODE: {
      const shape1 = s1 as IGeoShapePostalCode
      const shape2 = s2 as IGeoShapePostalCode
      return shape1.postal_code === shape2.postal_code
    }
    case SHAPE_TYPES.STATE: {
      const shape1 = s1 as IGeoShapeState
      const shape2 = s2 as IGeoShapeState
      return shape1.state_shape_id === shape2.state_shape_id
    }
    case SHAPE_TYPES.COUNTRY: {
      const shape1 = s1 as IGeoShapeCountry
      const shape2 = s2 as IGeoShapeCountry
      return shape1.country_shape_id === shape2.country_shape_id
    }
    case SHAPE_TYPES.METROPOLITAN: {
      const shape1 = s1 as IGeoShapeMetropolitan
      const shape2 = s2 as IGeoShapeMetropolitan
      return shape1.shape_id === shape2.shape_id
    }
  }
}

export function isShapeAlreadySelected(selectedShape: IGeoShape, form?: IForm): boolean {
  const {
    geoTargets: { cities, postalCodes, states, countries, metropolitans }
  } = form || getForm()
  let shapeArray: IGeoShape[]
  switch (selectedShape.type) {
    case SHAPE_TYPES.CITY: {
      shapeArray = cities
      break
    }
    case SHAPE_TYPES.POSTAL_CODE: {
      shapeArray = postalCodes
      break
    }
    case SHAPE_TYPES.STATE: {
      shapeArray = states
      break
    }
    case SHAPE_TYPES.COUNTRY: {
      shapeArray = countries
      break
    }
    case SHAPE_TYPES.METROPOLITAN: {
      shapeArray = metropolitans
      break
    }
  }
  return shapeArray.find(c => areEqualShapes(c, selectedShape)) !== undefined
}

export function getNewFormForShapes(
  form: IForm,
  shapeType: SHAPE_TYPES,
  shapes: IGeoShape[],
  relationShapes?: IGeoShape[]
): IForm {
  if (!relationShapes) {
    return {
      ...form,
      geoTargets: setFormShapesAndGetGeoTargets({ ...form.geoTargets }, shapes, shapeType)
    }
  } else {
    return {
      ...form,
      geoTargets: setFormShapesAndGetGeoTargets({ ...form.geoTargets }, shapes, shapeType),
      context: {
        ...form.context,
        relations: setRelationShapesAndGetRelations({ ...form.context.relations }, shapes, shapeType)
      }
    }
  }
}

function getSelectionForShapeType(selection: IGeoTargetsSelection, shapeType: SHAPE_TYPES): string[] {
  switch (shapeType) {
    case SHAPE_TYPES.CITY: {
      return selection.cityIds
    }
    case SHAPE_TYPES.POSTAL_CODE: {
      return selection.postalCodeIds
    }
    case SHAPE_TYPES.STATE: {
      return selection.stateIds
    }
    case SHAPE_TYPES.COUNTRY: {
      return selection.countryIds
    }
    case SHAPE_TYPES.METROPOLITAN: {
      return selection.metropolitanIds
    }
  }
}

function setSelectionForShapeType(selection: IGeoTargetsSelection, shapeType: SHAPE_TYPES, selectedIds: string[]) {
  switch (shapeType) {
    case SHAPE_TYPES.CITY: {
      selection.cityIds = selectedIds
      break
    }
    case SHAPE_TYPES.POSTAL_CODE: {
      selection.postalCodeIds = selectedIds
      break
    }
    case SHAPE_TYPES.STATE: {
      selection.stateIds = selectedIds
      break
    }
    case SHAPE_TYPES.COUNTRY: {
      selection.countryIds = selectedIds
      break
    }
    case SHAPE_TYPES.METROPOLITAN: {
      selection.metropolitanIds = selectedIds
      break
    }
  }
  return selection
}

function getFormShapes(form: IForm, shapeType: SHAPE_TYPES): IGeoShape[] {
  switch (shapeType) {
    case SHAPE_TYPES.CITY: {
      return form.geoTargets.cities
    }
    case SHAPE_TYPES.POSTAL_CODE: {
      return form.geoTargets.postalCodes
    }
    case SHAPE_TYPES.STATE: {
      return form.geoTargets.states
    }
    case SHAPE_TYPES.COUNTRY: {
      return form.geoTargets.countries
    }
    case SHAPE_TYPES.METROPOLITAN: {
      return form.geoTargets.metropolitans
    }
  }
}

function setFormShapesAndGetGeoTargets(
  geoTargets: IFormGeoTargets,
  shapes: IGeoShape[],
  shapeType: SHAPE_TYPES
): IFormGeoTargets {
  switch (shapeType) {
    case SHAPE_TYPES.CITY: {
      geoTargets.cities = shapes as IGeoShapeCity[]
      break
    }
    case SHAPE_TYPES.POSTAL_CODE: {
      geoTargets.postalCodes = shapes as IGeoShapePostalCode[]
      break
    }
    case SHAPE_TYPES.STATE: {
      geoTargets.states = shapes as IGeoShapeState[]
      break
    }
    case SHAPE_TYPES.COUNTRY: {
      geoTargets.countries = shapes as IGeoShapeCountry[]
      break
    }
    case SHAPE_TYPES.METROPOLITAN: {
      geoTargets.metropolitans = shapes as IGeoShapeMetropolitan[]
      break
    }
  }
  return geoTargets
}

function getShapeRelations(form: IForm, shapeType: SHAPE_TYPES): IGeoShape[] {
  switch (shapeType) {
    case SHAPE_TYPES.CITY: {
      return form.context.relations.cities
    }
    case SHAPE_TYPES.POSTAL_CODE: {
      return form.context.relations.postalCodes
    }
    case SHAPE_TYPES.STATE: {
      return form.context.relations.states
    }
    case SHAPE_TYPES.COUNTRY: {
      return form.context.relations.countries
    }
    case SHAPE_TYPES.METROPOLITAN: {
      return form.context.relations.metropolitans
    }
  }
}

function setRelationShapesAndGetRelations(
  relations: IFormRelations,
  shapes: IGeoShape[],
  shapeType: SHAPE_TYPES
): IFormRelations {
  switch (shapeType) {
    case SHAPE_TYPES.CITY: {
      relations.cities = shapes as IGeoShapeCity[]
      break
    }
    case SHAPE_TYPES.POSTAL_CODE: {
      relations.postalCodes = shapes as IGeoShapePostalCode[]
      break
    }
    case SHAPE_TYPES.STATE: {
      relations.states = shapes as IGeoShapeState[]
      break
    }
    case SHAPE_TYPES.COUNTRY: {
      relations.countries = shapes as IGeoShapeCountry[]
      break
    }
    case SHAPE_TYPES.METROPOLITAN: {
      relations.metropolitans = shapes as IGeoShapeMetropolitan[]
      break
    }
  }
  return relations
}

function getSameShapeInArray(shape: IGeoShape, shapes: IGeoShape[]): IGeoShape {
  switch (shape.type) {
    case SHAPE_TYPES.CITY: {
      const city: IGeoShapeCity = shape as IGeoShapeCity
      const cities: IGeoShapeCity[] = shapes as IGeoShapeCity[]
      const foundCity = cities.find(c => c.city_shape_id === city.city_shape_id)
      if (!foundCity) {
        throw Error(`No city found for id="${city.city_shape_id}"`)
      }
      return foundCity
    }
    default: {
      // implement all types when multi-select will be implemented
      throw Error(`getSameShapeInArray for type ${shape.type} not yet supported`)
    }
  }
}

async function addAllShapesForType(
  form: IForm,
  type: SHAPE_TYPES,
  shapes: IGeoShape[],
  campaignId: number
): Promise<IGeoShape[]> {
  switch (type) {
    case SHAPE_TYPES.CITY: {
      const toAddCities = shapes as IGeoShapeCity[]
      const toAddCityIds = toAddCities.map(c => c.city_shape_id)
      const allCities = [...form.geoTargets.cities, ...toAddCities]
      const allCitiesFromServer = (await shapeRPO.setAssociatedCities(campaignId, allCities)) as IGeoShapeCity[]
      return allCitiesFromServer.filter(c => toAddCityIds.includes(c.city_shape_id))
    }
    default: {
      throw Error(`Multiple add for type ${type} not yet supported`)
    }
  }
}

async function createNewShapeEntity(shape: IGeoShape, campaignId: number): Promise<IGeoShape> {
  switch (shape.type) {
    case SHAPE_TYPES.CITY: {
      return shapeRPO.associateCityToCampaign(campaignId, shape as IGeoShapeCity) as Promise<IGeoShape>
    }
    case SHAPE_TYPES.POSTAL_CODE: {
      return shapeRPO.associatePostalCodeToCampaign(campaignId, shape as IGeoShapePostalCode) as Promise<IGeoShape>
    }
    case SHAPE_TYPES.STATE: {
      return shapeRPO.associateStateToCampaign(campaignId, shape as IGeoShapeState) as Promise<IGeoShape>
    }
    case SHAPE_TYPES.COUNTRY: {
      return shapeRPO.associateCountryToCampaign(campaignId, shape as IGeoShapeCountry) as Promise<IGeoShape>
    }
    case SHAPE_TYPES.METROPOLITAN: {
      const metropolitan: IGeoShapeMetropolitan = shape as IGeoShapeMetropolitan
      await addShapes(metropolitan.cities, true)
      // cities of this metropolitan will be hidden in the list
      return metropolitan
    }
  }
}

async function deleteShapeEntity(shape: IGeoShape, campaignId: number): Promise<void> {
  switch (shape.type) {
    case SHAPE_TYPES.CITY: {
      return shapeRPO.deassociateCityToCampaign(campaignId, shape.id)
    }
    case SHAPE_TYPES.POSTAL_CODE: {
      return shapeRPO.deassociatePostalCodeToCampaign(campaignId, shape.id)
    }
    case SHAPE_TYPES.STATE: {
      return shapeRPO.deassociateStateToCampaign(campaignId, shape.id)
    }
    case SHAPE_TYPES.COUNTRY: {
      return shapeRPO.deassociateCountryToCampaign(campaignId, shape.id)
    }
    case SHAPE_TYPES.METROPOLITAN: {
      const metropolitan: IGeoShapeMetropolitan = shape as IGeoShapeMetropolitan
      await deleteShapes(metropolitan.cities)
      // cities of this metropolitan will be hidden in the list
      return
    }
  }
}

export function hasShapes(form: IForm): boolean {
  return (
    form.geoTargets.cities.length !== 0 ||
    form.geoTargets.states.length !== 0 ||
    form.geoTargets.postalCodes.length !== 0 ||
    form.geoTargets.countries.length !== 0 ||
    form.geoTargets.metropolitans.length !== 0
  )
}
